import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, ExternalLink, X, Upload } from 'lucide-react';
import api, { mediaUrl, uploadImage, uploadContentMedia, errText } from '../../lib/api';
import RichTextEditor from '../../components/RichTextEditor';
import { useToast } from '../../hooks/use-toast';

const BLANK = { title: '', category: 'Blog', excerpt: '', content: '', cover_image: '', audio_url: '', video_url: '', seo_title: '', seo_description: '', author: 'Osho Universal', tags: [], is_published: true };

const AdminBlog = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | post | null

  const fetchAll = async () => {
    setLoading(true);
    try { const res = await api.get('/admin/blog/posts'); setItems(res.data || []); }
    catch (e) { console.error('Failed to load blog posts:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const remove = async (p) => {
    if (!window.confirm('Delete this post?')) return;
    try { await api.delete(`/admin/blog/posts/${p.id}`); toast({ title: 'Deleted' }); fetchAll(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  if (editing) return <PostEditor post={editing === 'new' ? null : editing} onDone={() => { setEditing(null); fetchAll(); }} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif-display text-4xl text-burgundy">Blog & <span className="font-italic-display text-rose-italic">News</span></h1>
          <p className="mt-2 text-mahogany/80">Publish poems, stories and articles. Changes appear instantly on the public blog.</p>
        </div>
        <button onClick={() => setEditing('new')} className="pill-btn" data-testid="new-post-btn"><Plus className="w-4 h-4" /> New Post</button>
      </div>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : items.length === 0 ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40">No posts yet.</div>
        : (
          <ul className="space-y-3" data-testid="admin-blog-list">
            {items.map((p) => (
              <li key={p.id} className="p-4 rounded-2xl border border-rose/50 bg-white/70 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-post-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {p.cover_image && <img src={mediaUrl(p.cover_image)} alt="" className="w-14 h-14 rounded-lg object-cover" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif-display text-lg text-burgundy truncate">{p.title}</span>
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{p.category}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.is_published ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{p.is_published ? 'Published' : 'Draft'}</span>
                    </div>
                    <div className="text-xs text-mahogany/60 mt-0.5">/{p.slug} · {p.author}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.is_published && <IconBtn onClick={() => window.open(`/blog/${p.slug}`, '_blank')} title="Preview"><ExternalLink className="w-4 h-4" /></IconBtn>}
                  <IconBtn onClick={() => setEditing(p)} title="Edit"><Pencil className="w-4 h-4" /></IconBtn>
                  <IconBtn onClick={() => remove(p)} title="Delete" danger><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

const PostEditor = ({ post, onDone, onCancel }) => {
  const { toast } = useToast();
  const [f, setF] = useState(post ? { ...BLANK, ...post } : BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadCover = async (file) => {
    try { const url = await uploadImage(file); set('cover_image', url); toast({ title: 'Image uploaded' }); }
    catch (e) { console.error('Upload failed:', e); toast({ title: 'Upload failed', description: errText(e, 'Please try a different image.') }); }
  };

  const uploadAudio = async (file) => {
    try { const { url } = await uploadContentMedia(file); set('audio_url', url); toast({ title: 'Audio uploaded' }); }
    catch (e) { toast({ title: 'Audio upload failed', description: errText(e) }); }
  };

  const save = async () => {
    if (!f.title) return toast({ title: 'Title is required' });
    setSaving(true);
    const payload = {
      title: f.title, category: f.category, excerpt: f.excerpt, content: f.content,
      cover_image: f.cover_image, audio_url: f.audio_url || null, video_url: f.video_url || null,
      seo_title: f.seo_title || null, seo_description: f.seo_description || null, author: f.author,
      tags: Array.isArray(f.tags) ? f.tags : [], is_published: f.is_published,
    };
    try {
      if (post) await api.patch(`/admin/blog/posts/${post.id}`, payload);
      else await api.post('/admin/blog/posts', payload);
      toast({ title: post ? 'Post updated' : 'Post published' });
      onDone();
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail }); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="post-editor">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-3xl text-burgundy">{post ? 'Edit Post' : 'New Post'}</h1>
        <button onClick={onCancel} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
      </div>

      <Txt label="Title *" value={f.title} onChange={(e) => set('title', e.target.value)} testid="post-title" />
      <div className="grid sm:grid-cols-2 gap-3">
        <Txt label="Category" value={f.category} onChange={(e) => set('category', e.target.value)} testid="post-category" />
        <Txt label="Author" value={f.author} onChange={(e) => set('author', e.target.value)} />
      </div>
      <Txt label="Excerpt" value={f.excerpt} onChange={(e) => set('excerpt', e.target.value)} testid="post-excerpt" />

      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Cover Image</span>
        <div className="flex items-center gap-2">
          {f.cover_image ? <img src={mediaUrl(f.cover_image)} alt="" className="w-20 h-14 rounded-lg object-cover" /> : <div className="w-20 h-14 rounded-lg bg-rose-soft/60 flex items-center justify-center"><Upload className="w-4 h-4 text-mahogany/60" /></div>}
          <label className="text-xs pill-btn-outline cursor-pointer py-1.5">Upload<input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])} /></label>
          {f.cover_image && <button onClick={() => set('cover_image', '')} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Content</span>
        <RichTextEditor value={f.content} onChange={(html) => set('content', html)} />
      </div>

      <Txt label="Tags (comma separated)" value={(f.tags || []).join(', ')} onChange={(e) => set('tags', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />

      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Audio blog (optional)</span>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs pill-btn-outline cursor-pointer py-1.5">Upload audio<input type="file" accept="audio/*" className="hidden" onChange={(e) => e.target.files[0] && uploadAudio(e.target.files[0])} /></label>
          {f.audio_url && <audio controls src={mediaUrl(f.audio_url)} className="h-9" />}
          {f.audio_url && <button onClick={() => set('audio_url', '')} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      <Txt label="Vlog video URL (YouTube / Vimeo, optional)" value={f.video_url} onChange={(e) => set('video_url', e.target.value)} />

      <div className="rounded-xl border border-rose/30 bg-rose-soft/20 p-4 space-y-3">
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80">Search / SEO</span>
        <Txt label="SEO Title (≤ 60 chars)" value={f.seo_title} onChange={(e) => set('seo_title', e.target.value)} testid="post-seo-title" />
        <Txt label="Meta Description (≤ 155 chars)" value={f.seo_description} onChange={(e) => set('seo_description', e.target.value)} testid="post-seo-desc" />
      </div>

      <label className="flex items-center gap-2 text-sm text-burgundy">
        <input type="checkbox" checked={f.is_published} onChange={(e) => set('is_published', e.target.checked)} data-testid="post-published-toggle" /> Published (visible on public blog)
      </label>

      <div className="flex gap-3 pt-2">
        <button onClick={save} disabled={saving} className="pill-btn justify-center flex-1" data-testid="post-save-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {post ? 'Save Changes' : 'Publish Post'}</button>
        <button onClick={onCancel} className="pill-btn-outline">Cancel</button>
      </div>
    </div>
  );
};

const Txt = ({ label, testid, ...p }) => (
  <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <input {...p} data-testid={testid} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>
);
const IconBtn = ({ children, danger, ...p }) => (
  <button {...p} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${danger ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-rose-soft/60 text-mahogany hover:bg-rose-soft'}`}>{children}</button>
);

export default AdminBlog;
